import React, { useState } from 'react';
import './CarePlusDeliveryHistory.css';
import * as translator from '../../../../utils/translator';
import * as http from '../../../../utils/http';
import { toLocaleDateString } from '../../../../utils/helpers';
import { PLAN_TYPE_LABEL } from './carePlusConstants';
import CopyToClipboard from '../../../../components/copyToClipboard/copyToClipboard';
import HistoryBoxIcon from '../../../../icons/careplus-history-box.svg';
import DoctorIcon from '../../../../icons/careplus-detail-doctor.svg';
import PharmacyIcon from '../../../../icons/careplus-detail-pharmacy.svg';
import AddressIcon from '../../../../icons/careplus-detail-address.svg';
import TruckIcon from '../../../../icons/careplus-detail-truck.svg';
import ArrowDownIcon from '../../../../icons/ArrowDown.svg';
import CardIcon from '../../../../icons/careplus-detail-card.svg';

interface CarePlusDeliveryHistoryProps {
  items: CarePlusDeliveryHistoryItem[];
  country: string;
  language: string;
  isMonthlyPlan?: boolean;
}

interface ExpandedOrderData {
  doctorName?: string | null;
  pharmacyDetails?: string[];
  deliveryAddress?: string[];
  paymentMethod?: { paymentMethodTitle?: string; brand?: string; last4?: string; expiryDate?: string };
  paidAt?: string | null;
  orderShipping?: { contractor?: string | null; tracking_id?: string | null; tracking_url?: string | null };
  shippingMethod?: string;
}

const DetailItem: React.FC<{ icon: string; label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ icon, label, value, sub }) => (
  <div className="cp-history-card__detail">
    <span className="cp-history-card__detail-icon"><img src={icon} alt="" /></span>
    <div>
      <p className="cp-history-card__detail-label">{label}</p>
      <p className="cp-history-card__detail-value">{value}</p>
      {sub && <p className="cp-history-card__detail-sub">{sub}</p>}
    </div>
  </div>
);

const CarePlusDeliveryHistoryCard: React.FC<{
  item: CarePlusDeliveryHistoryItem;
  country: string;
  language: string;
  isMonthlyPlan?: boolean;
}> = ({ item, country, language, isMonthlyPlan }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedData, setExpandedData] = useState<ExpandedOrderData | null>(null);
  const [loading, setLoading] = useState(false);

  const t = (path: string) => translator.translate(language, 'my_account', path);
  const locale = translator.getLocaleByCountry(country);
  const dateOptions = { year: 'numeric' as const, month: 'long' as const, day: 'numeric' as const };

  const handleExpand = () => {
    if (!expanded && !expandedData) {
      setLoading(true);
      http.getFromServer(`${country}/account/orders/${item.orderNumber}/details`, [
        http.MIDDLEWARES.CUSTOMER_ID,
      ])
        .then((res: any) => {
          setExpandedData(res.data?.orderData ?? {});
        })
        .catch(() => setExpandedData({}))
        .finally(() => setLoading(false));
    }
    setExpanded((prev) => !prev);
  };

  const planTypeLabel = isMonthlyPlan
    ? t('CarePlus/monthlyPlan/label')
    : PLAN_TYPE_LABEL[item.planType as 'basic' | 'pro' | 'max' | 'monthly'] ?? item.planType;
  const orderLabel = (t('CarePlus/historyCard/order') || '').replace(':n', String(item.cycleNumber));
  const trackingId = item.trackingId ?? expandedData?.orderShipping?.tracking_id ?? null;
  const trackingUrl = item.trackingUrl ?? expandedData?.orderShipping?.tracking_url ?? null;

  const isDelivered = item.status === 'delivered';
  const isShippedStatus = ['shipped', 'sent_to_customer'].includes(item.status);
  const statusText = isDelivered
    ? t('CarePlus/historyCard/delivered')
    : isShippedStatus
      ? t('CarePlus/historyCard/shipped')
      : translator.translate(language, 'my_account', `MyOrders/status/${item.status}`) || (item.status || '').replace(/_/g, ' ');

  return (
    <div className="cp-history-card">
      <div className="cp-history-card__head">
        <div className="cp-history-card__id">
          <span className="cp-history-card__icon-box"><img src={HistoryBoxIcon} alt="" /></span>
          <div>
            <p className="cp-history-card__order-no">{orderLabel}</p>
            <p className="cp-history-card__date">{toLocaleDateString(item.date, language, dateOptions)}</p>
          </div>
        </div>
        {item.status && (
          <span className={`cp-history-card__shipped${(isShippedStatus || isDelivered) ? '' : ' cp-history-card__shipped--neutral'}`}>
            <i className="cp-history-card__shipped-dot" />
            <span className="cp-history-card__shipped-text">{statusText}</span>
          </span>
        )}
      </div>

      <div className="cp-history-card__medrow">
        <div className="cp-history-card__col">
          <p className="cp-history-card__label">{t('CarePlus/historyCard/medication')}</p>
          <p className="cp-history-card__value">{item.medicationName}</p>
        </div>
        <div className="cp-history-card__col cp-history-card__col--right">
          <p className="cp-history-card__label">{t('CarePlus/historyCard/plan')}</p>
          <p className="cp-history-card__value">{planTypeLabel}</p>
        </div>
      </div>

      <button type="button" className="cp-history-card__toggle" onClick={handleExpand}>
        {expanded ? t('CarePlus/historyCard/collapse') : t('CarePlus/historyCard/viewFullOrder')}
        <img src={ArrowDownIcon} alt="" className={expanded ? 'is-open' : ''} />
      </button>

      {expanded && (
        loading ? (
          <p className="cp-history-card__loading">...</p>
        ) : (
          <>
            <div className="cp-history-card__grid">
              <div className="cp-history-card__gridcol">
                {expandedData?.doctorName && (
                  <DetailItem icon={DoctorIcon} label={t('CarePlus/historyCard/doctor')} value={t('CarePlus/detail/glp1Doctor')} />
                )}
                {expandedData?.pharmacyDetails && expandedData.pharmacyDetails.length > 0 && (
                  <DetailItem
                    icon={PharmacyIcon}
                    label={t('CarePlus/historyCard/pharmacy')}
                    value={expandedData.pharmacyDetails[0]}
                    sub={expandedData.pharmacyDetails.slice(1).join(', ') || undefined}
                  />
                )}
                {expandedData?.deliveryAddress && expandedData.deliveryAddress.length > 0 && (
                  <DetailItem
                    icon={AddressIcon}
                    label={t('CarePlus/historyCard/delivery')}
                    value={expandedData.deliveryAddress[0]}
                    sub={expandedData.deliveryAddress.slice(1).join(', ')}
                  />
                )}
              </div>
              <div className="cp-history-card__gridcol">
                {trackingId && (
                  <DetailItem
                    icon={TruckIcon}

                    label={t('CarePlus/historyCard/trackingId')}
                    value={
                      <span className="cp-history-card__tracking">
                        <a href={trackingUrl ?? '#'} target="_blank" rel="noreferrer">
                          {expandedData?.shippingMethod ?? 'DHL'}
                        </a>
                        <span className="cp-history-card__track-pill">
                          {trackingId}
                          <CopyToClipboard textToCopy={trackingId} />
                        </span>
                      </span>
                    }
                  />
                )}
                {expandedData?.paymentMethod?.paymentMethodTitle && (
                  <DetailItem
                    icon={CardIcon}
                    label={t('CarePlus/historyCard/payment')}
                    value={
                      expandedData.paymentMethod.brand && expandedData.paymentMethod.last4
                        ? `${expandedData.paymentMethod.brand.charAt(0).toUpperCase() + expandedData.paymentMethod.brand.slice(1)} •••• ${expandedData.paymentMethod.last4}`
                        : expandedData.paymentMethod.paymentMethodTitle
                    }
                    sub={expandedData.paidAt ? t('CarePlus/historyCard/captured').replace(':date', toLocaleDateString(expandedData.paidAt, language, dateOptions)) : undefined}
                  />
                )}
              </div>
            </div>
            <div className="cp-history-card__prices">
              {/* Shipping is bundled into Care+ Plan; no separate delivery row. */}
              {item.prices && ([
                ['CarePlus/historyCard/medicationPrice', item.prices.drug],
                ['CarePlus/historyCard/planFee', item.prices.carePlusPlan],
                ['MyOrders/details/discount', item.prices.discount],
              ] as const).filter(([, value]) => value != null).map(([labelKey, value]) => (
                <div key={labelKey} className="cp-history-card__price-row"><span>{t(labelKey)}</span><span>{translator.getPrice(value as number, locale)}</span></div>
              ))}
              <div className="cp-history-card__price-row cp-history-card__price-row--total"><span>{t('CarePlus/historyCard/totalAmount')}</span><span>{translator.getPrice(item.total, locale)}</span></div>
            </div>
          </>
        )
      )}
    </div>
  );
};

const CarePlusDeliveryHistory: React.FC<CarePlusDeliveryHistoryProps> = ({ items, country, language, isMonthlyPlan }) => {
  return (
    <div className="careplus-delivery-history">
      {items.map((item, index) => (
        <CarePlusDeliveryHistoryCard
          key={`${item.orderNumber}-${index}`}
          item={item}
          country={country}
          language={language}
          isMonthlyPlan={isMonthlyPlan}
        />
      ))}
    </div>
  );
};

export default CarePlusDeliveryHistory;
